import { Injectable, LOCALE_ID, inject } from "@angular/core";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { HalfPageContainer } from "@pdf/helpers/container/half-page/half-page-container";
import { DraegerFonts } from "@pdf/helpers/draeger-fonts";
import { ContainerProperties, FontNames, Headlines, ImagePaths, PdfProperties } from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";

@Injectable({
  providedIn: "root",
})
export class LegendPagePdfService {
  private localeId = inject(LOCALE_ID);
  private userService = inject(UserService);

  private scope!: BusinessScope;
  private headlines = new Headlines();

  constructor() {
    this.userService.businessScope$.subscribe((scope) => {
      this.scope = scope;
    });
  }

  generate(pdf: jsPDF, container?: HalfPageContainer) {
    if (container) {
      container.toggleActiveSide();
    }
    this.addFonts(pdf);
    pdf.setFont(FontNames.DRAEGER_PANGEA);
    pdf.setTextColor(PdfProperties.DRAEGERBLUE);
    pdf.setFontSize(PdfProperties.HEADLINE_FONT_SIZE);
    pdf.text(this.headlines.LEGEND, container?.xPosition || ContainerProperties.LEFT_X, PdfProperties.HEADLINE_Y, {
      baseline: "top",
    });
    let imagePath: ImagePaths;
    let srcWidth: number;
    let srcHeight: number;
    if (this.scope === BusinessScope.MONITORING) {
      imagePath = ImagePaths.LEGEND_MONITORING_EN;
      srcWidth = 987;
      srcHeight = 958;
    } else {
      const localeImageMap: Partial<Record<string, ImagePaths>> = {
        "de-DE": ImagePaths.LEGEND_DE,
        "es-ES": ImagePaths.LEGEND_ES,
        "fr-FR": ImagePaths.LEGEND_FR,
        "pt-PT": ImagePaths.LEGEND_PT,
      };
      imagePath = localeImageMap[this.localeId] ?? ImagePaths.LEGEND_EN;
      srcWidth = 987;
      srcHeight = 1098;
    }
    const [width, height] = this.fitImageToFrame(srcWidth, srcHeight, ContainerProperties.WIDTH, ContainerProperties.HEIGHT);
    pdf.addImage(imagePath, "PNG", container?.xPosition || ContainerProperties.LEFT_X, ContainerProperties.Y, width, height);
  }

  private addFonts(doc: jsPDF) {
    new DraegerFonts().fonts.forEach((font) => {
      doc.addFileToVFS(font.ttfName, font.base64);
      doc.addFont(font.ttfName, font.fontName, font.fontWeight);
    });
  }

  private fitImageToFrame(srcWidth: number, srcHeight: number, maxWidth: number, maxHeight: number) {
    const ratio: number = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return [srcWidth * ratio, srcHeight * ratio];
  }
}
